import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import transporteService, { Transporte } from '../services/transporteService';

interface TransporteSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (transporte: Transporte) => void;
}

const TransporteSearch: React.FC<TransporteSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [transportes, setTransportes] = useState<Transporte[]>([]);
    const [transportesFiltrados, setTransportesFiltrados] = useState<Transporte[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadTransportes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const items = await transporteService.getTransportes();
            setTransportes(items);
            setTransportesFiltrados(items);
            if (items.length === 0) {
                setError('No se encontraron transportes.');
            }
        } catch (err: any) {
            setError(`Error al cargar transportes: ${err.message || 'Error desconocido'}`);
            setTransportes([]);
            setTransportesFiltrados([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            setTransportesFiltrados(transportes);
            setError(null);
            return;
        }

        const trimmedLower = trimmed.toLowerCase();
        // Buscar por número de expreso, nombre o código
        const filtered = transportes.filter(t => {
            const nroExpreso = String(t.nroExpreso || '').includes(trimmed);
            const nombre = t.nombre?.toLowerCase().includes(trimmedLower) || t.nombreExpreso?.toLowerCase().includes(trimmedLower);
            const codigo = String(t.codigo || '').includes(trimmed);
            return nroExpreso || nombre || codigo;
        });

        setTransportesFiltrados(filtered);
        if (filtered.length === 0) {
            setError('No se encontraron transportes.');
        } else {
            setError(null);
        }
    }, [searchTerm, transportes]);

    const handleSelect = (transporte: Transporte) => {
        onSelect(transporte);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setTransportesFiltrados(transportes);
        setError(null);
        onClose();
    };

    // Cargar transportes cuando se abre el diálogo
    useEffect(() => {
        if (open) {
            loadTransportes();
        } else {
            setSearchTerm('');
            setTransportes([]);
            setTransportesFiltrados([]);
            setError(null);
        }
    }, [open, loadTransportes]);

    // Filtrar cuando cambia el término de búsqueda
    useEffect(() => {
        if (open && transportes.length > 0) {
            handleSearch();
        }
    }, [open, searchTerm, transportes, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Transporte
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de expreso, nombre o código"
                        inputProps={{ 'aria-label': 'Buscar transporte' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && transportesFiltrados.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {transportesFiltrados.map((transporte, index) => {
                                        const key = transporte.nroExpreso || transporte.id || index;
                                        return (
                                            <Card 
                                                key={key} 
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: 3
                                                    }
                                                }}
                                                onClick={() => handleSelect(transporte)}
                                            >
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                            Nro: {transporte.nroExpreso || transporte.codigo || '-'}
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelect(transporte);
                                                            }}
                                                            sx={{ minWidth: 'auto', px: 2 }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </Box>
                                                    <Divider sx={{ my: 1.5 }} />
                                                    <Stack spacing={1}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Nombre
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {transporte.nombre?.trim() || transporte.nombreExpreso?.trim() || '-'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Expreso</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transportesFiltrados.map((transporte, index) => {
                                            const key = transporte.nroExpreso || transporte.id || index;
                                            return (
                                                <TableRow 
                                                    key={key} 
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelect(transporte)}
                                                >
                                                    <TableCell>{transporte.nroExpreso || transporte.codigo || '-'}</TableCell>
                                                    <TableCell>{transporte.nombre?.trim() || transporte.nombreExpreso?.trim() || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelect(transporte);
                                                            }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && transportesFiltrados.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron transportes
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransporteSearch;

